import type { BreadcrumbItemType } from "@/shared/ui/Breadcrumb";

const SHOP_HOME = "/app/shops/$shopId" as const;
const REPORTS = "/app/shops/$shopId/reports" as const;
const CATALOG = "/app/shops/$shopId/catalog" as const;
const INVENTORY = "/app/shops/$shopId/inventory" as const;
const PURCHASE_ORDERS = "/app/shops/$shopId/purchase-orders" as const;
const ROLES = "/app/shops/$shopId/roles" as const;
const STAFF = "/app/shops/$shopId/staff" as const;
const PRODUCT_CLAIMS = "/app/shops/$shopId/product-claims" as const;
const PROMOTIONS = "/app/shops/$shopId/promotions" as const;
function shopParams(
  shopId: string | undefined,
): Record<string, string> | undefined {
  return shopId ? { shopId } : undefined;
}

function home(shopId: string | undefined): BreadcrumbItemType {
  return { label: "ຮ້ານ", to: SHOP_HOME, params: shopParams(shopId) };
}

/** Breadcrumb ສຳລັບໜ້າພາຍໃຕ້ /app/shops/$shopId — ລຳດັບ: ຮ້ານ → … → ໜ້າປັດຈຸບັນ (ບໍ່ມີ to) */
export const shopBreadcrumbs = {
  catalog(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ສິນຄ້າທີ່ຂາຍໄດ້" }];
  },

  services(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ບໍລິການ" }];
  },

  catalogRequests(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      { label: "ສິນຄ້າທີ່ຂາຍໄດ້", to: CATALOG, params: shopParams(shopId) },
      { label: "ຄຳຂໍເພີ່ມສິນຄ້າ" },
    ];
  },

  catalogSkuEdit(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      { label: "ສິນຄ້າທີ່ຂາຍໄດ້", to: CATALOG, params: shopParams(shopId) },
      { label: "ແກ້ໄຂລາຄາ/ລະຫັດ" },
    ];
  },

  inventory(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ສາງ" }];
  },

  inventoryReceive(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      { label: "ສາງ", to: INVENTORY, params: shopParams(shopId) },
      { label: "ຮັບເຂົ້າສາງ" },
    ];
  },

  inventoryTransfer(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      { label: "ສາງ", to: INVENTORY, params: shopParams(shopId) },
      { label: "ໂອນສະຕ໋ອກລະຫວ່າງສາຂາ" },
    ];
  },

  inventoryReserveRelease(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      { label: "ສາງ", to: INVENTORY, params: shopParams(shopId) },
      { label: "ຈອງ ແລະ ປົດຈອງ" },
    ];
  },

  inventoryMovements(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      { label: "ສາງ", to: INVENTORY, params: shopParams(shopId) },
      { label: "ປະຫວັດການເຄື່ອນໄຫວສະຕ໋ອກ" },
    ];
  },

  inventoryLocations(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      { label: "ສາງ", to: INVENTORY, params: shopParams(shopId) },
      { label: "ຕຳແໜ່ງສາງ" },
    ];
  },

  /** ລາຍລະອຽດ SKU ຕໍ່ສາຂາ — params: shopProductSkuId, search branchId */
  inventorySkuDetail(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      { label: "ສາງ", to: INVENTORY, params: shopParams(shopId) },
      { label: "ລາຍລະອຽດສະຕ໋ອກ" },
    ];
  },

  purchaseOrders(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ໃບສັ່ງຊື້" }];
  },

  suppliers(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ຜູ້ສະໜອງ" }];
  },

  purchaseOrderDetail(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      {
        label: "ໃບສັ່ງຊື້",
        to: PURCHASE_ORDERS,
        params: shopParams(shopId),
      },
      { label: "ລາຍລະອຽດ" },
    ];
  },

  /** ຮັບຂອງຕາມ PO — poNumber ສຳລັບຫົວຂໍ້ breadcrumb */
  purchaseOrderReceive(
    shopId: string | undefined,
    poNumber?: string | null,
  ): BreadcrumbItemType[] {
    return [
      home(shopId),
      {
        label: "ໃບສັ່ງຊື້",
        to: PURCHASE_ORDERS,
        params: shopParams(shopId),
      },
      {
        label: poNumber?.trim()
          ? `${poNumber.trim()} · ຮັບຂອງ`
          : "ຮັບຂອງຕາມໃບສັ່ງຊື້",
      },
    ];
  },

  pos(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ຂາຍ" }];
  },

  posSales(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      {
        label: "ຂາຍ",
        to: "/app/shops/$shopId/pos",
        params: shopParams(shopId),
      },
      { label: "ປະຫວັດບິນ" },
    ];
  },

  promotions(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ໂປຣໂມຊັນ" }];
  },

  workshop(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ງານສ້ອມ" }];
  },

  workshopQuotes(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      {
        label: "ງານສ້ອມ",
        to: "/app/shops/$shopId/workshop",
        params: shopParams(shopId),
      },
      { label: "ໃບປະເມີນລາຄາ" },
    ];
  },

  workshopJobNew(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      {
        label: "ງານສ້ອມ",
        to: "/app/shops/$shopId/workshop",
        params: shopParams(shopId),
      },
      { label: "ສ້າງໃບງານ" },
    ];
  },

  workshopJobDetail(
    shopId: string | undefined,
    jobNumber: string | undefined,
  ): BreadcrumbItemType[] {
    return [
      home(shopId),
      {
        label: "ງານສ້ອມ",
        to: "/app/shops/$shopId/workshop",
        params: shopParams(shopId),
      },
      { label: jobNumber ?? "ໃບງານ" },
    ];
  },

  promotionsNew(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      {
        label: "ໂປຣໂມຊັນ",
        to: PROMOTIONS,
        params: shopParams(shopId),
      },
      { label: "ສ້າງໂປຣ" },
    ];
  },

  promotionsEdit(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      {
        label: "ໂປຣໂມຊັນ",
        to: PROMOTIONS,
        params: shopParams(shopId),
      },
      { label: "ແກ້ໄຂໂປຣ" },
    ];
  },

  marketplace(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ຕະຫຼາດອອນລາຍ" }];
  },

  notifications(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ການແຈ້ງເຕືອນ" }];
  },

  marketplaceOrders(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      {
        label: "ຕະຫຼາດອອນລາຍ",
        to: "/app/shops/$shopId/marketplace",
        params: shopParams(shopId),
      },
      { label: "ຄຳສັ່ງຊື້" },
    ];
  },

  productClaims(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ເຄສສິນຄ້າມີບັນຫາ" }];
  },

  productClaimsNew(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      {
        label: "ເຄສສິນຄ້າມີບັນຫາ",
        to: PRODUCT_CLAIMS,
        params: shopParams(shopId),
      },
      { label: "ສ້າງເຄສ" },
    ];
  },

  productClaimsEdit(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      {
        label: "ເຄສສິນຄ້າມີບັນຫາ",
        to: PRODUCT_CLAIMS,
        params: shopParams(shopId),
      },
      { label: "ແກ້ໄຂຮ່າງ" },
    ];
  },

  productClaimsDetail(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      {
        label: "ເຄສສິນຄ້າມີບັນຫາ",
        to: PRODUCT_CLAIMS,
        params: shopParams(shopId),
      },
      { label: "ລາຍລະອຽດເຄສ" },
    ];
  },

  roles(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ບົດບາດ" }];
  },

  roleForm(shopId: string | undefined, isEdit: boolean): BreadcrumbItemType[] {
    return [
      home(shopId),
      { label: "ບົດບາດ", to: ROLES, params: shopParams(shopId) },
      { label: isEdit ? "ແກ້ໄຂບົດບາດ" : "ສ້າງບົດບາດໃໝ່" },
    ];
  },

  branches(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ສາຂາ" }];
  },

  staff(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ພະນັກງານ" }];
  },

  shiftCalendar(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ລາຍການກະ" }];
  },

  shiftMy(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ກະຂອງຂ້ອຍ" }];
  },

  shiftReport(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ລາຍງານກະ" }];
  },

  staffForm(shopId: string | undefined, isEdit: boolean): BreadcrumbItemType[] {
    return [
      home(shopId),
      { label: "ພະນັກງານ", to: STAFF, params: shopParams(shopId) },
      { label: isEdit ? "ແກ້ໄຂພະນັກງານ" : "ເພີ່ມພະນັກງານ" },
    ];
  },

  /** ແຜງຄວບຄຸມຮ້ານ (/app/shops/$shopId) */
  dashboard(): BreadcrumbItemType[] {
    return [{ label: "ແຜງຄວບຄຸມ" }];
  },

  /** ຕັ້ງຄ່າຮ້ານ + ອັດຕາແລກ — /app/shops/$shopId/settings */
  settings(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ຕັ້ງຄ່າຮ້ານ" }];
  },

  /** ລາຍງານ — /app/shops/$shopId/reports */
  reports(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ລາຍງານ" }];
  },

  reportsBranches(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      { label: "ລາຍງານ", to: REPORTS, params: shopParams(shopId) },
      { label: "ແຍກສາຂາ" },
    ];
  },

  reportsInventory(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      { label: "ລາຍງານ", to: REPORTS, params: shopParams(shopId) },
      { label: "ສາງ & ສະຕ໋ອກ" },
    ];
  },

  reportsWarehouseLocations(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      { label: "ລາຍງານ", to: REPORTS, params: shopParams(shopId) },
      {
        label: "ສາງ & ສະຕ໋ອກ",
        to: "/app/shops/$shopId/reports/inventory",
        params: shopParams(shopId),
      },
      { label: "ຕຳແໜ່ງສາງ" },
    ];
  },

  /** P6+ — /app/shops/$shopId/reports/modules */
  reportsModules(shopId: string | undefined): BreadcrumbItemType[] {
    return [
      home(shopId),
      { label: "ລາຍງານ", to: REPORTS, params: shopParams(shopId) },
      { label: "ໂມດູນອື່ນ" },
    ];
  },

  /** P7 — /app/shops/$shopId/settlement */
  settlement(shopId: string | undefined): BreadcrumbItemType[] {
    return [home(shopId), { label: "ຊຳລະຮ້ານ" }];
  },
};
